import React, { useCallback } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import type { ComparePdfViewMode, PreviousApplication } from '../types';
import PdfPanel from './PdfPanel';
import styles from './styles';
/* eslint-disable react/require-default-props */
interface ComparePdfsModalProps {
  open: boolean;
  onClose: () => void;
  previousApplication: PreviousApplication | null;
  previousPdfUrl: string;
  previousPdfLoading: boolean;
  previousPdfError: string;
  isLoadingPdfModal: boolean;
  currentPdfUrl: string;
  currentPdfLoading?: boolean;
  currentPdfError?: string;
  appKey: string;
  pdfViewMode: ComparePdfViewMode;
  // eslint-disable-next-line no-unused-vars
  onPdfViewModeChange: (mode: ComparePdfViewMode) => void;
}

const ComparePdfsModal = ({
  open,
  onClose,
  previousApplication,
  previousPdfUrl,
  previousPdfLoading,
  previousPdfError,
  isLoadingPdfModal,
  currentPdfUrl,
  currentPdfLoading = false,
  currentPdfError = '',
  appKey,
  pdfViewMode,
  onPdfViewModeChange
}: ComparePdfsModalProps) => {
  const handleTabsChange = useCallback(
    (_: React.SyntheticEvent, value: string | number | null) => {
      if (value != null) onPdfViewModeChange(value as ComparePdfViewMode);
    },
    [onPdfViewModeChange]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: styles.dialogPaper }}>
      <Box sx={styles.root}>
        <Box sx={styles.header}>
          <Typography variant='h6' sx={styles.title}>
            Compare PDFs
          </Typography>
          <Tabs
            value={pdfViewMode}
            onChange={handleTabsChange}
            variant='fullWidth'
            sx={styles.tabs}>
            <Tab value='clinical_studies' label='Clinical Studies (Section 14)' />
            <Tab value='adverse_reactions' label='Adverse Reactions (Section 6.1)' />
            <Tab value='full_label' label='Full Label' />
          </Tabs>
        </Box>
        <Box sx={styles.content}>
          {isLoadingPdfModal && (
            <Box sx={styles.centerContent}>
              <CircularProgress />
            </Box>
          )}
          {!isLoadingPdfModal && !previousApplication && (
            <Box sx={styles.centerContent}>
              <Typography color='text.secondary'>No previous submission to compare.</Typography>
            </Box>
          )}
          {!isLoadingPdfModal && previousApplication && (
            <Box sx={styles.panelsContainer}>
              <Stack direction='row' spacing={2} sx={styles.panelsStack}>
                <PdfPanel
                  title={`Previous — ${previousApplication.applno}-${previousApplication.submission_type}-${previousApplication.submission_number}`}
                  pdfUrl={previousPdfUrl}
                  isLoading={previousPdfLoading}
                  iframeTitle='Previous submission label'
                  error={previousPdfError}
                />
                <PdfPanel
                  title={`Current — ${appKey}`}
                  pdfUrl={currentPdfUrl}
                  isLoading={currentPdfLoading}
                  iframeTitle='Current submission label'
                  error={currentPdfError}
                  emptyMessage='No current label loaded'
                />
              </Stack>
            </Box>
          )}
        </Box>
        <Box sx={styles.footer}>
          <Button onClick={onClose} variant='contained'>
            Close
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ComparePdfsModal;
